import React, { useEffect, useRef, useState, useCallback } from "react";
import * as Tone from "tone";
import { v4 as uuidv4 } from "uuid";
import EventEmitter from "events";
import WaveformPlaylist from "waveform-playlist";
import { saveAs } from "file-saver";
import { useAudioEditor } from "../../../../context/AudioEditorContext";
import AudioEditorTrackSidebarItem from "./AudioEditorTrackSidebarItem";
import AudioEditorTrackSplitLine from "./AudioEditorTrackSplitLine";
import noteIcon from "../../../../assets/images/note.png";
import { ReactComponent as MuteIcon } from "../../../../assets/images/volume-mute-line.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/images/delete-bin-line.svg";
import { ReactComponent as ArrowRightLineIcon } from "../../../../assets/images/arrow-right-line.svg";
import { useTranslation } from "react-i18next";
import { ZOOM_LEVELS, DEFAULT_ZOOM_INDEX, SAMPLES_PER_PIXELS } from "../constants";
import { convertWavToMp3 } from "../../../../utils/audioConversion";
import {
    addMp3BlobToProject,
    buildComposition,
    fetchEditorSession,
    nocache,
    resolveMixVoices,
    resolveSourceAudioAIId,
    stripNocache,
} from "../utils";
import { AudioEditorTrackType } from "../../../../types/types";
import useKeyboardShortcut from "../../../../hooks/useKeyboardShortcut";
import useAudioEditorNotifications from "../hooks/useAudioEditorNotifications";
import styles from "./AudioEditorTrackMixer.module.css";

// Create a global object to hold the current export filename
// This ensures we can access it immediately without waiting for context updates
export const currentExportFilename = { value: "" };

const EmptyState: React.FC<{ message: string }> = ({ message }) => <div className={styles.previewEmpty}>{message}</div>;

const AudioEditorTracksMixer: React.FC = () => {
    const [ee] = useState(() => new EventEmitter());
    const [toneCtx, setToneCtx] = useState<Tone.Context | null>(null);
    const setUpChain = useRef<Promise<void>[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const playlistRef = useRef<any>(null);
    const syncDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [clicked, setClicked] = useState(false);
    const { notifyError, notifyWarning, notifyInfo, notifySuccess } = useAudioEditorNotifications();

    const {
        tracks,
        registerActions,
        addTrack,
        removeTrack,
        deleteTrack,
        addToProjectSettings,
        sessionAudio,
        setSessionAudio,
        setIsProcessing,
        mode,
        setMode,
        isPlaying,
        setIsPlaying,
        playAudio,
        pauseAudio,
        setTracks,
        removeFades,
        normalizeOnExport,
        targetLufs,
    } = useAudioEditor();

    // Ref are use to be accessed by the the event listeners
    const renderActionRef = useRef<"download" | "upload">("download");
    const addToProjectSettingsRef = useRef(addToProjectSettings);
    const normalizeOnExportRef = useRef(normalizeOnExport);
    const targetLufsRef = useRef(targetLufs);
    // The render listener is registered once, so it reaches the session by ref.
    const sessionAudioRef = useRef(sessionAudio);

    // We need to manually keep the project settings up to date
    useEffect(() => {
        addToProjectSettingsRef.current = addToProjectSettings;
    }, [addToProjectSettings]);

    useEffect(() => {
        sessionAudioRef.current = sessionAudio;
    }, [sessionAudio]);

    useEffect(() => {
        normalizeOnExportRef.current = normalizeOnExport;
    }, [normalizeOnExport]);

    useEffect(() => {
        targetLufsRef.current = targetLufs;
    }, [targetLufs]);

    const { t: rawT } = useTranslation();
    const t = rawT as any;

    useEffect(() => {
        // @ts-ignore
        setToneCtx(Tone.getContext());
    }, []);

    /**
     * Read the timeline from the playlist, which is the source of truth: it is what the
     * renderer mixes down. Used both to refresh React state and, at export time, to build
     * the arrangement — so the saved composition always describes the saved audio, even if
     * the two had drifted apart.
     */
    const readTracksFromPlaylist = useCallback((): AudioEditorTrackType[] => {
        if (!playlistRef.current?.tracks?.length) return [];

        return playlistRef.current.tracks.map((playlistTrack: any) => {
            // Try to match by ID. This lookup reads the `tracks` captured by the closure,
            // which is one render behind right after a track is added — so it misses the
            // newest track every time.
            const stateTrack = tracks.find((t) => t.id === playlistTrack?._customId);

            // Provenance therefore comes from the playlist track itself, where it was
            // stamped at load, and is applied in both branches. Keeping it in a single
            // place is what stops a field from being silently dropped by a sync.
            const matchedTrack = {
                ...(stateTrack || {
                    id: playlistTrack?._customId || playlistTrack?.name || `track-${Date.now()}`,
                    name: playlistTrack.name,
                    url: playlistTrack.src,
                    description: "",
                }),
                ...(playlistTrack?._rwMeta || {}),
            };

            // Never keep the cache-busted url the playlist was loaded with.
            const finalUrl = matchedTrack?.url || stripNocache(playlistTrack?.src || "");
            const isMuted = playlistRef.current?.mutedTracks?.includes(playlistTrack);

            return {
                ...matchedTrack,
                url: finalUrl,
                cueIn: playlistTrack.cueIn,
                cueOut: playlistTrack.cueOut,
                gain: playlistTrack.gain,
                startTime: playlistTrack.startTime,
                endTime: playlistTrack.endTime,
                fadeIn: playlistTrack.fadeIn,
                fadeOut: playlistTrack.fadeOut,
                _appliedFades: playlistTrack._appliedFades,
                muted: isMuted,
            };
        });
    }, [tracks]);

    /**
     * Resolve once the playlist exists. It is created by a later effect than the one
     * registering the actions, and only after the Tone context is available.
     */
    const waitForPlaylist = useCallback(async () => {
        for (let attempt = 0; attempt < 60 && !playlistRef.current; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, 50));
        }
        return playlistRef.current;
    }, []);

    // Function to sync tracks from playlist to internal state
    const syncTracksFromPlaylist = useCallback(() => {
        if (!playlistRef.current?.tracks?.length) return;
        setTracks(readTracksFromPlaylist());
    }, [readTracksFromPlaylist, setTracks]);

    // The export listener is registered once, so it reaches the current reader by ref.
    const readTracksRef = useRef(readTracksFromPlaylist);
    useEffect(() => {
        readTracksRef.current = readTracksFromPlaylist;
    }, [readTracksFromPlaylist]);

    // Debounce ref and function for syncing
    const debouncedSync = useCallback(() => {
        if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
        syncDebounceRef.current = setTimeout(() => {
            syncTracksFromPlaylist();
        }, 300);
    }, [syncTracksFromPlaylist]);

    // Register toolbar actions
    useEffect(() => {
        if (!registerActions) return;

        registerActions({
            playAudio: () => {
                const playlist = playlistRef.current;
                if (!playlist) return;

                const selection = playlist.getTimeSelection();
                const start = selection?.start ?? 0;

                playlist.setTimeSelection(start, start); // ensure visual cursor too
                playlist.cursor = start; // logical cursor
                playlist.drawRequest();

                ee.emit("automaticscroll", true);
                ee.emit("play");
            },
            pauseAudio: () => {
                ee.emit("automaticscroll", false);
                ee.emit("pause");
            },
            stopAudio: () => {
                try {
                    ee.emit("automaticscroll", false);
                    ee.emit("rewind");

                    // Also reset cursor visually as a fallback
                    if (playlistRef.current) {
                        const start = 0;
                        playlistRef.current.setTimeSelection(start, start);
                        playlistRef.current.cursor = start;
                        playlistRef.current.drawRequest();
                    }
                } catch (error) {
                    notifyError(error);
                    setIsPlaying(false);
                }
            },
            exportAudio: () => {
                renderActionRef.current = "download";
                setIsProcessing(true);
                ee.emit("startaudiorendering", "wav");
            },
            exportAudioToProject: () => {
                renderActionRef.current = "upload";
                setIsProcessing(true);
                ee.emit("startaudiorendering", "wav");
            },
            newTrack: async (track: AudioEditorTrackType, index?: number) => {
                try {
                    setIsLoading(true);

                    // Actions are registered before the playlist is built (it also waits
                    // for the Tone context), so an early caller would hit a null ref and
                    // silently lose its clip. Wait for it instead.
                    if (!(await waitForPlaylist())) {
                        notifyError(new Error("Playlist is not ready"), t("trackLoadingError"));
                        return undefined;
                    }

                    const initialCount = playlistRef.current.tracks.length;

                    // Hand the whole clip to the loader rather than mutating the track
                    // afterwards. waveform-playlist applies cues, position, gain, mute and
                    // fades itself, in the order it needs (setCues before setFadeIn, which
                    // throws when the fade is longer than the clip). Post-hoc mutation had
                    // to replicate that order by hand and dropped clips when it got it wrong.
                    await playlistRef.current.load([
                        {
                            src: nocache(track.url),
                            name: track.name,
                            ...(track.startTime !== undefined ? { start: track.startTime } : {}),
                            ...(track.cueIn !== undefined ? { cuein: track.cueIn } : {}),
                            ...(track.cueOut !== undefined ? { cueout: track.cueOut } : {}),
                            ...(track.gain !== undefined ? { gain: track.gain } : {}),
                            ...(track.muted ? { muted: true } : {}),
                            ...(track.fadeIn
                                ? {
                                      fadeIn: {
                                          shape: track.fadeIn.shape || "linear",
                                          duration: track.fadeIn.duration,
                                      },
                                  }
                                : {}),
                            ...(track.fadeOut
                                ? {
                                      fadeOut: {
                                          shape: track.fadeOut.shape || "linear",
                                          duration: track.fadeOut.duration,
                                      },
                                  }
                                : {}),
                        },
                    ]);

                    const wasLoaded = playlistRef.current.tracks.length === initialCount + 1;

                    if (wasLoaded) {
                        // Get newly created track
                        let newTrack = playlistRef.current.tracks[playlistRef.current.tracks.length - 1];

                        // Handle insertion at specific index
                        if (index !== undefined) {
                            const movedTrack = playlistRef.current.tracks.pop();
                            if (!movedTrack) {
                                notifyError(t("failedToLoadTrack"), t("trackLoadingError"));
                                return undefined;
                            }

                            // Insert at specified position
                            playlistRef.current.tracks.splice(index, 0, movedTrack);
                            newTrack = playlistRef.current.tracks[index];
                        }

                        // Everything above was applied by the loader. All that is left is
                        // our own fade bookkeeping: the library overwrites fadeIn/fadeOut
                        // with its internal fade ids, so the editor keeps its own copy.
                        if (newTrack) {
                            if (track.fadeIn || track.fadeOut) {
                                newTrack._appliedFades = {
                                    ...(track.fadeIn
                                        ? { fadeIn: { start: track.fadeIn.start, duration: track.fadeIn.duration } }
                                        : {}),
                                    ...(track.fadeOut
                                        ? { fadeOut: { start: track.fadeOut.start, duration: track.fadeOut.duration } }
                                        : {}),
                                };
                            }

                            // Calculate peaks with all adjustments applied
                            newTrack.calculatePeaks(
                                playlistRef.current.samplesPerPixel,
                                playlistRef.current.sampleRate,
                            );

                            // Redraw with all tracks updated
                            playlistRef.current.adjustDuration();
                            playlistRef.current.drawRequest();
                        }

                        // Create new unique id
                        const trackId = uuidv4();
                        newTrack._customId = trackId; // Preserve id
                        // Preserve provenance (voice / music / sfx and its source asset) so
                        // the arrangement can be saved and replayed on a translation.
                        newTrack._rwMeta = {
                            role: track.role,
                            sourceKind: track.sourceKind,
                            sourceId: track.sourceId,
                            sourceText: track.sourceText,
                            // The arrangement is read back from the playlist at export
                            // time, so anything missing here is lost when a track cannot
                            // be matched to its React counterpart.
                            sourceVoice: track.sourceVoice,
                            sourceDialogLines: track.sourceDialogLines,
                            lines: track.lines,
                        };

                        // Add to track state
                        addTrack({ ...track, id: trackId }, index);

                        // Sync all tracks after loading
                        syncTracksFromPlaylist();

                        // The caller's id is not reused on purpose (the same library sound
                        // can be laid twice and each clip needs its own), so hand back the
                        // one that was assigned: it is the only way to find this clip again.
                        return trackId;
                    } else {
                        // The asset could not be loaded — most often because the file is
                        // no longer on S3. The clip is simply not laid down, and the
                        // message names it so the user knows which one is missing rather
                        // than discovering a hole in the timeline.
                        notifyWarning(t("trackCouldNotBeLoaded", { name: track.name }));
                    }
                } catch (error) {
                    notifyError(error, t("trackLoadingError"));
                } finally {
                    setIsLoading(false);
                }
                return undefined;
            },
            deleteTrack: (index: number) => {
                if (playlistRef?.current?.tracks?.[index]) {
                    if (playlistRef?.current?.tracks.length === 1) {
                        setIsPlaying(false);
                    }
                    ee.emit("removeTrack", playlistRef.current.tracks[index]);
                    removeTrack(index);
                    // Redraw with all tracks updated
                    playlistRef.current.adjustDuration();
                    playlistRef.current.drawRequest();
                }
            },
            zoomIn: () => {
                if (playlistRef.current) {
                    ee.emit("zoomin");
                }
            },
            zoomOut: () => {
                if (playlistRef.current) {
                    ee.emit("zoomout");
                }
            },
            setActiveTrack: (index: number) => {
                if (playlistRef.current) {
                    const track = playlistRef.current?.tracks?.[index];
                    playlistRef.current.setActiveTrack(track);
                }
            },
            setTrackVolume: (volumePercent: number) => {
                const playlist = playlistRef.current;
                const track = playlist?.getActiveTrack();
                if (!track) return;

                // Convert % back to gain: 0% → 1.0, 50% → 1.5, -50% → 0.5
                const gain = 1 + volumePercent / 100;
                track.setGainLevel(gain);

                debouncedSync();
            },
            getTrackVolume: (index: number): number => {
                const track = playlistRef.current?.tracks?.[index];
                const gain = track?.gain ?? 1;
                return (gain - 1) * 100; // e.g., gain = 1.5 → 50%
            },
            removeFades: (index: number) => {
                const track = playlistRef.current?.tracks?.[index];
                // Remove previous fade in
                track.removeFade(track.fadeIn);
                track.fadeIn = undefined;
                // Remove previous fade out
                track.removeFade(track.fadeOut);
                track.fadeOut = undefined;
                delete track._appliedFades;
                // Sync
                syncTracksFromPlaylist();
                playlistRef.current.drawRequest();
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        ee,
        registerActions,
        removeTrack,
        setIsProcessing,
        setIsLoading,
        addTrack,
        syncTracksFromPlaylist,
        waitForPlaylist,
        notifyError,
        notifyWarning,
    ]);

    // Init playlist only once
    useEffect(() => {
        if (!containerRef.current || !toneCtx || playlistRef.current) return;

        const playlist = WaveformPlaylist(
            {
                ac: toneCtx.rawContext,
                samplesPerPixel: SAMPLES_PER_PIXELS,
                mono: true,
                waveHeight: 90,
                // Discrete bars (mockup edition-b) instead of a continuous outline
                barWidth: 2,
                barGap: 2,
                container: containerRef.current,
                state: "select",
                colors: {
                    // Teal fill around peaks; channel bg shows through as light bars
                    waveOutlineColor: "#4a7d85",
                    // Tick marks on the time scale (must contrast on white)
                    timeColor: "#4b5655",
                },
                controls: {
                    show: false,
                    width: 150,
                },
                zoomLevels: ZOOM_LEVELS,
                timescale: true,
                seekStyle: "line",
                draggable: true,
            },
            // @ts-ignore
            ee,
        );

        playlistRef.current = playlist;

        playlistRef.current.setZoomIndex(DEFAULT_ZOOM_INDEX);
        playlistRef.current.initExporter();

        // Sync once the loader has appended its tracks. "audiosourcesloaded" fires before
        // `playlist.tracks` is updated (Playlist.js emits it at the top of the .then, the
        // concat happens at the bottom), so syncing on it always read a stale list.
        ee.on("audiosourcesrendered", () => {
            syncTracksFromPlaylist();
        });

        // The loader swallows its failures in its own .catch, so keep the raw error
        // available for diagnosis. It is not notified here: every load goes through
        // newTrack, which reports the failure naming the clip that is missing.
        ee.on("audiosourceserror", (error: unknown) => {
            console.error("[audioEditor] audio source failed to load:", error);
        });

        ee.on("pause", () => {
            try {
                playlistRef.current?.tracks?.forEach((track: any) => {
                    if (track?.source && typeof track.source.disconnect === "function") {
                        track.source.disconnect();
                    }
                    // Clear the source to prevent re-disconnect attempt
                    track.source = null;
                });
            } catch (err) {
                notifyError(err, t("unexpectedError"));
            }
        });

        ee.on("rewind", () => {
            try {
                playlistRef.current?.tracks?.forEach((track: any) => {
                    if (track?.source && typeof track.source.disconnect === "function") {
                        track.source.disconnect();
                    }
                    track.source = null;
                });
            } catch (err) {
                notifyError(err, t("unexpectedError"));
            }
        });

        ee.on("splittrack", async (time: number) => {
            const playlist = playlistRef.current;
            const track = playlist.getActiveTrack();

            if (!track || !time) {
                notifyWarning(t("pleaseSelectTrackToSplit"));
                return;
            }

            const cueIn = track.cueIn;
            const cueOut = track.cueOut;
            const trackStart = track.getStartTime();
            const trackEnd = track.getEndTime();
            const originalIndex = playlist.tracks.indexOf(track);

            if (time <= trackStart || time >= trackEnd) {
                notifyWarning(t("splitPointOutOfRange"));
                return;
            }

            // Convert timeline split time to cue-relative time
            const splitCueTime = cueIn + (time - trackStart);
            if (splitCueTime <= cueIn || splitCueTime >= cueOut) {
                notifyWarning(t("invalidSplitPosition"));
                return;
            }

            // 1. Trim original track (first part)
            playlistRef.current.setTimeSelection(trackStart, time);
            ee.emit("trim", track);

            // 2. Create second copy of the original track
            const newTrackId = `${track.name}-split-${Date.now()}`;
            // Both halves still come from the same asset.
            const splitMeta = track._rwMeta || {};
            const newTrackData = {
                id: newTrackId,
                url: track.src,
                name: newTrackId,
                description: "",
                ...splitMeta,
            };

            await playlist.load([
                {
                    src: track.src,
                    name: newTrackId,
                    gain: track.gain,
                },
            ]);

            const newTrack = playlist.tracks.pop();
            if (!newTrack) {
                notifyError(new Error(t("failedToLoadSplitTrack")), t("trackSplitError"));
                return;
            }

            // Insert into track list
            newTrack._customId = newTrackId;
            newTrack._rwMeta = splitMeta;
            playlist.tracks.splice(originalIndex + 1, 0, newTrack);
            playlist.setActiveTrack(newTrack);

            // Trim second half using cue-relative times
            newTrack.trim(splitCueTime, cueOut);
            newTrack.calculatePeaks(playlist.samplesPerPixel, playlist.sampleRate);
            newTrack.setStartTime(time); // place it visually at split point

            // Redraw
            playlist.adjustDuration();
            playlist.setTimeSelection(time, time); // Move selection cursor to split time
            playlist.drawRequest();

            addTrack(newTrackData, originalIndex + 1);
            syncTracksFromPlaylist();
        });

        ee.on("automaticscroll", (val) => {
            playlistRef.current.isAutomaticScroll = val;
        });

        // Add synchronization after drag operations
        ee.on("shift", debouncedSync);

        ee.on("audiorenderingstarting", (offlineCtx: any, a: any[]) => {
            const offlineContext = new Tone.OfflineContext(offlineCtx);
            Tone.setContext(offlineContext);
            setUpChain.current = a;
        });

        ee.on("audiorenderingfinished", async (type: string, data: Blob) => {
            Tone.setContext(toneCtx);

            if (type !== "wav") return;

            try {
                const mp3Blob = await convertWavToMp3(
                    data,
                    (status) => {
                        console.log(status.message, status.progress ?? "");
                    },
                    {
                        normalize: normalizeOnExportRef.current,
                        targetLufs: targetLufsRef.current,
                    },
                );

                if (!mp3Blob || mp3Blob.size === 0) {
                    throw new Error("MP3 blob is empty or undefined after conversion");
                }

                if (renderActionRef.current === "upload") {
                    const token = localStorage.getItem("token");
                    const settings = addToProjectSettingsRef.current;

                    if (!settings) {
                        throw new Error("Upload settings are missing (addToProjectSettingsRef is null)");
                    }

                    const { name, projectId, language, targetAudioAIId, linesByClip } = settings;

                    // Updating an existing audio keeps its project, so projectId is only
                    // required when creating a new entry.
                    if (!token || !name || !language || (!projectId && !targetAudioAIId)) {
                        throw new Error("Missing required upload params: token, name, projectId, or language");
                    }

                    try {
                        // From the playlist, not from React state: the render came from
                        // the playlist, so the arrangement must describe the same thing.
                        const currentTracks = readTracksRef.current();
                        const session = sessionAudioRef.current;
                        await addMp3BlobToProject({
                            mp3Blob,
                            token,
                            name,
                            language,
                            projectId,
                            // The only input: the transcript of the audio and its dialog
                            // lines are both derived from the composition server-side.
                            composition: buildComposition(currentTracks, linesByClip),
                            targetAudioAIId,
                            // A brand new mix records which capsule its voice came from, so a
                            // later translation can find this sound design again.
                            sourceAudioAIId: resolveSourceAudioAIId(currentTracks),
                            // Who is heard, so the project keeps it and the translation
                            // screen can map the voices instead of asking for an unknown one.
                            ...(() => {
                                const voices = resolveMixVoices(currentTracks);
                                return voices.voiceAIId || voices.voiceCloneId || voices.voicePromptId
                                    ? voices
                                    : {
                                          voiceAIId: session?.voiceAIId,
                                          voiceCloneId: session?.voiceCloneId,
                                          voicePromptId: session?.voicePromptId,
                                      };
                            })(),
                        });
                        notifySuccess(t("mp3UploadedSuccessfully"));

                        // The session still describes what was loaded before this save, so
                        // reopening the modal would show the previous version. Re-read it,
                        // which is also how newly created replies get their real ids.
                        if (targetAudioAIId) {
                            const refreshed = await fetchEditorSession(token, targetAudioAIId);
                            if (refreshed) setSessionAudio(refreshed);

                            syncTracksFromPlaylist();
                        }
                    } catch (uploadError) {
                        notifyError(uploadError, t("uploadProjectAttachmentFailed"));
                    }
                } else {
                    // Get the filename from the direct reference or use default
                    const activeFilename =
                        (currentExportFilename.value && currentExportFilename.value.trim()) || "export";
                    saveAs(mp3Blob, `${activeFilename}.mp3`);
                }
            } catch (err) {
                notifyError(err, t("mp3ConversionExportFailed"));
                try {
                    // Get the filename from the direct reference or use default
                    const activeFilename =
                        (currentExportFilename.value && currentExportFilename.value.trim()) || "export";

                    // Fallback to raw WAV download with filename from direct reference
                    saveAs(data, `${activeFilename}.wav`);
                } catch (fallbackErr) {
                    notifyError(fallbackErr, t("fallbackWavDownloadFailed"));
                }
            } finally {
                // Clear the ref after download is complete
                currentExportFilename.value = "";
                // Reset processing state when everything is done
                setIsProcessing(false);
            }
        });

        return () => {
            // Depending on the events package version off or removeListener is available
            // This should prevent future bugs if package is automatically changed
            if (ee.off) {
                ee.off("shift", debouncedSync);
            } else {
                ee.removeListener("shift", debouncedSync);
            }

            if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
            // remove all listeners
            ee.removeAllListeners("audiosourcesrendered");
            ee.removeAllListeners("audiosourceserror");
            ee.removeAllListeners("pause");
            ee.removeAllListeners("rewind");
            ee.removeAllListeners("splittrack");
            ee.removeAllListeners("automaticscroll");
            ee.removeAllListeners("shift");
            ee.removeAllListeners("audiorenderingstarting");
            ee.removeAllListeners("audiorenderingfinished");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ee, toneCtx, setIsProcessing, notifyError, notifyWarning]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);

            // Safely cleanup audio contexts and connections when unmounting
            if (playlistRef.current) {
                try {
                    // Pause any playing audio
                    ee.emit("pause");

                    // Safely clean up tracks with the same defensive approach
                    if (playlistRef.current.tracks) {
                        playlistRef.current.tracks.forEach((track: any) => {
                            try {
                                // Defensively disconnect audio nodes
                                if (track?.source && typeof track.source.disconnect === "function") {
                                    track.source.disconnect();
                                }
                                track.source = null;
                            } catch (e) {
                                notifyError(e, t("unexpectedError"));
                            }
                        });
                    }
                } catch (e) {
                    notifyError(e, t("unexpectedError"));
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifyError]);

    const handleMuteTrack = (index: number) => {
        if (playlistRef?.current?.tracks?.[index]) {
            const track = playlistRef.current.tracks[index];
            ee.emit("mute", track);

            // Update track muted state in our state
            const updatedTracks = [...tracks];
            updatedTracks[index] = {
                ...updatedTracks[index],
                muted: !updatedTracks[index].muted,
            };
            setTracks(updatedTracks);
        }
    };

    // Track whether tracks are playing to remove select cursor
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        if (isPlaying) {
            setClicked(false);
        }

        const handleClick = () => {
            if (!isPlaying && mode !== "drag") {
                setClicked(true);
            }
        };

        container.addEventListener("click", handleClick);
        return () => container.removeEventListener("click", handleClick);
    }, [isPlaying, mode]);

    useEffect(() => {
        const el = containerRef.current;
        if (mode !== "split" || !el || !playlistRef.current) return;

        const handleClick = (e: MouseEvent) => {
            const cursorTime = playlistRef.current.cursor;
            ee.emit("splittrack", cursorTime);
            setMode(null);
        };

        el.addEventListener("click", handleClick);
        return () => el.removeEventListener("click", handleClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        const container = containerRef.current;
        const line = document.getElementById("cursor-follow-line");

        if (!container || mode !== "split" || !line) return;

        const handleMouseMove = (e: MouseEvent) => {
            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            line.style.left = `${x}px`;
        };

        container.addEventListener("mousemove", handleMouseMove);

        return () => {
            container.removeEventListener("mousemove", handleMouseMove);
        };
    }, [mode]);

    useEffect(() => {
        const container = containerRef.current;
        const playlist = playlistRef.current;
        if (!container || mode !== "fadeIn" || !playlist) return;

        ee.emit("fadetype", "linear");

        const handleClick = () => {
            try {
                const track = playlist.getActiveTrack();
                if (!track) {
                    notifyWarning(t("pleaseSelectTrack"));
                    return;
                }

                const trackStart = track.getStartTime();
                const trackEnd = track.getEndTime();
                const cursorTime = playlist.cursor;

                // Ensure cursor is within track boundaries
                if (cursorTime <= trackStart || cursorTime > trackEnd) {
                    notifyWarning(t("cursorMustBeWithinTrack"));
                    return;
                }

                // Calculate duration with safety margin
                let duration = cursorTime - trackStart;

                // Ensure minimum valid duration (at least 0.1 seconds)
                const MIN_DURATION = 0.1;
                if (duration < MIN_DURATION) {
                    notifyWarning(t("fadeInDurationMustBePositive"));
                    return;
                }

                // Track and check applied fades
                track._appliedFades = track._appliedFades || {};
                const fadeOut = track._appliedFades.fadeOut;

                if (fadeOut && fadeOut.start < cursorTime && cursorTime < fadeOut.start + fadeOut.duration) {
                    notifyWarning(t("fadeInOverlapsWithFadeOut"));
                    return;
                }

                // Store applied fade with validated parameters
                const fadeIn = {
                    shape: "linear",
                    start: trackStart,
                    duration: duration,
                };

                notifyInfo(t("applyingFadeIn"));

                // Remove previous fade in
                track.removeFade(track.fadeIn);
                track.fadeIn = undefined;

                // First assign to the track's storage for our app
                track._appliedFades.fadeIn = {
                    start: trackStart,
                    duration: duration,
                };

                // Then assign to the track for the waveform library
                track.fadeIn = fadeIn;

                // Emit the event with validated parameters
                ee.emit("fadein", duration, track);

                // Sync and reset mode
                syncTracksFromPlaylist();
                setMode(null);
            } catch (error) {
                notifyError(error, t("fadeInError"));
                setMode(null);
            }
        };

        container.addEventListener("click", handleClick);
        return () => container.removeEventListener("click", handleClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, syncTracksFromPlaylist, setMode, t, notifyWarning, notifyError, notifyInfo]);

    useEffect(() => {
        const container = containerRef.current;
        const playlist = playlistRef.current;
        if (!container || mode !== "fadeOut" || !playlist) return;

        ee.emit("fadetype", "linear");

        const handleClick = () => {
            try {
                const track = playlist.getActiveTrack();
                if (!track) {
                    notifyWarning(t("pleaseSelectTrack"));
                    return;
                }

                const trackEnd = track.getEndTime();
                const trackStart = track.getStartTime();
                const cursorTime = playlist.cursor;

                // Ensure cursor is within track boundaries
                if (cursorTime < trackStart || cursorTime >= trackEnd) {
                    notifyWarning(t("cursorMustBeWithinTrack"));
                    return;
                }

                // Calculate duration with safety margin
                let duration = trackEnd - cursorTime;

                // Ensure minimum valid duration (at least 0.1 seconds)
                const MIN_DURATION = 0.1;
                if (duration < MIN_DURATION) {
                    notifyWarning(t("fadeOutDurationMustBePositive"));
                    return;
                }

                // Track and check applied fades
                track._appliedFades = track._appliedFades || {};
                const fadeIn = track._appliedFades.fadeIn;

                if (fadeIn && fadeIn.start < cursorTime + duration && fadeIn.start + fadeIn.duration > cursorTime) {
                    notifyWarning(t("fadeOutOverlapsWithFadeIn"));
                    return;
                }

                // Remove previous fade out
                track.removeFade(track.fadeOut);
                track.fadeOut = undefined;

                // Store applied fade with validated parameters
                const fadeOut = {
                    shape: "linear",
                    start: cursorTime,
                    duration: duration,
                };

                notifyInfo(t("applyingFadeOut"));

                // First assign to the track's storage for our app
                track._appliedFades.fadeOut = {
                    start: cursorTime,
                    duration: duration,
                };

                // Then assign to the track for the waveform library
                track.fadeOut = fadeOut;

                // Emit the event with validated parameters
                ee.emit("fadeout", duration, track);

                // Sync and reset mode
                syncTracksFromPlaylist();
                setMode(null);
            } catch (error) {
                notifyError(error, t("fadeOutError"));
                setMode(null);
            }
        };

        container.addEventListener("click", handleClick);
        return () => container.removeEventListener("click", handleClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, syncTracksFromPlaylist, setMode, t, notifyWarning, notifyError, notifyInfo]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !playlistRef.current) return;
        if (mode === "drag") {
            ee.emit("statechange", "shift");
        } else {
            ee.emit("statechange", "select");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useKeyboardShortcut("Escape", () => {
        setMode(null);
        // Prevent auto focus on escaped button
        const active = document.activeElement;
        if (active instanceof HTMLElement) {
            active.blur();
        }
    });

    useKeyboardShortcut(
        "Space",
        () => {
            try {
                if (isPlaying) {
                    pauseAudio?.();
                } else {
                    playAudio?.();
                }
                setIsPlaying(!isPlaying);
            } catch (error) {
                notifyError(error, t("unexpectedError"));
                // Reset the playback state if there was an error
                setIsPlaying(false);
            }
        },
        { preventDefault: true },
    );

    return (
        <>
            {!tracks.length && !isLoading && <EmptyState message={t("noTrackCreated")} />}
            <div className={`${styles.trackMixer} ${!tracks.length ? styles.hidden : ""}`}>
                {isLoading ? (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingIndicator}>{t("loading")}</div>
                    </div>
                ) : (
                    <div className={styles.tracksSidebar}>
                        {tracks.map((track: any, index: number) => (
                            <AudioEditorTrackSidebarItem
                                key={index}
                                trackLabel={track.name || `Track ${index + 1}`}
                                noteIcon={noteIcon}
                                icons={[
                                    {
                                        icon: DeleteIcon,
                                        title: t("delete"),
                                        onClick: () => deleteTrack?.(index),
                                        isSelected: false,
                                    },
                                    {
                                        icon: ArrowRightLineIcon,
                                        title: t("removeFades"),
                                        onClick: () => removeFades?.(index),
                                        isSelected: false,
                                        disabled: !track.fadeIn && !track.fadeOut ? t("noFadesToRemove") : undefined,
                                    },
                                    {
                                        icon: MuteIcon,
                                        title: t("mute"),
                                        onClick: () => handleMuteTrack(index),
                                        id: "mute",
                                        isSelected: track.muted,
                                    },
                                ]}
                                trackIndex={index}
                                track={track}
                                iconSize={20}
                                role={track.role}
                            />
                        ))}
                    </div>
                )}
                <div
                    ref={containerRef}
                    className={`${styles.mixerContainer}
            ${mode === "split" ? styles.mixerContainerSplitMode : ""}
            ${mode === "drag" ? styles.mixerContainerDragMode : ""}
            ${isPlaying ? styles.mixerContainerPlaying : ""}
            ${!isPlaying && !clicked ? styles.mixerContainerPaused : ""}
            ${clicked ? styles.mixerContainerClicked : ""}
          `}
                >
                    {mode === "split" && <AudioEditorTrackSplitLine />}
                </div>
            </div>
        </>
    );
};

export default AudioEditorTracksMixer;
